'use client'

import { useMemo, useState } from 'react'
import { ArticleRows } from '@/components/blocks/ArticleRows'
import { Band, Section } from '@/components/site/Band'
import { track } from '@/lib/analytics'
import type { Article } from '@/lib/portfolio'

/**
 * The blog listing, filtered by category.
 *
 * Client-side because the whole blog is a handful of articles that the page
 * already holds: a request per category click would be slower than the render
 * it replaces, and it would lose the reader's place in the list.
 */
export function BlogIndex({ articles, lede }: { articles: Article[]; lede: string }) {
    const [category, setCategory] = useState('Alles')

    const categories = useMemo(() => {
        const names = new Set(articles.map((article) => article.category).filter((name) => name !== ''))

        return ['Alles', ...names]
    }, [articles])

    const shown =
        category === 'Alles' ? articles : articles.filter((article) => article.category === category)

    return (
        <>
            {/* The filter sits beside the lede, in the head of the page: it is
             * how the design frames the blog — a shelf you pick from — rather
             * than a control bar over a list. */}
            <Section>
                <Band>
                    <div className="px-gutter pt-33 pb-10 max-lg:pt-18">
                        <h1 className="m-0 max-w-[14ch] text-[clamp(38px,5.2vw,84px)] leading-none font-normal tracking-[-0.05em] text-balance max-lg:text-[clamp(32px,8.6vw,48px)]">
                            Wat ik onderweg tegenkom<span className="ml-[0.04em] tracking-normal">.</span>
                        </h1>
                    </div>
                    <div className="grid grid-cols-[7fr_5fr] items-end gap-16 px-gutter pb-22 max-lg:grid-cols-1 max-lg:gap-7 max-lg:pb-12">
                        <p
                            className="m-0 max-w-[50ch] animate-rise-in text-[17px] leading-relaxed text-pretty text-dim"
                            style={{ animationDelay: '260ms' }}
                        >
                            {lede}
                        </p>
                        <div
                            className="flex animate-rise-in flex-wrap gap-2"
                            style={{ animationDelay: '400ms' }}
                        >
                            {categories.map((name) => (
                                <button
                                    key={name}
                                    type="button"
                                    onClick={() => {
                                        setCategory(name)
                                        track({ event: 'blog_filter', filter_value: name })
                                    }}
                                    aria-pressed={category === name}
                                    className={`cursor-pointer rounded-full border px-[15px] py-2 font-mono text-[11.5px] transition duration-200 ${
                                        category === name
                                            ? 'border-accent text-accent'
                                            : 'border-hair text-dim hover:border-accent hover:text-accent'
                                    }`}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>
                    </div>
                </Band>
            </Section>

            <Section edge="none">
                <Band>
                    <ArticleRows articles={shown} />
                </Band>
            </Section>
        </>
    )
}
